import { stringify } from 'query-string'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createValuationPdf as clientCreateValuationPdf,
  getSignedUrl as clientGetSignedUrl,
  SignedUrlResponse,
  ValuationPdfResponse,
} from '../../clients/valuationPdfClient'
import { Lead } from '../../@types/lead'
import { Address } from '../../@types/address'
import { PropertyLocation } from '../../@types/location'
import { Resource } from '../../@types/resource'
import { Intent } from '../../@types/intent'
import { isOwnerOrPartialOwner } from '../../utils/ownership'
import { ValuationPdf } from '../../@types/valuationPdf'
import { reportTrackingEvent } from '../../tracking/thunks'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../reducers'
import { enrichLead } from '../lead/thunks'

interface State {
  lead: Lead
  address: Resource<Address>
  location: Resource<PropertyLocation>
  valuationPdf: Resource<ValuationPdf>
}

interface ThunkStateConfig {
  state: ReduxState
}

const CMP_CREATIVE_SELL_WITH_AGENT = 'pdf_request_btn_happy'
const CMP_CREATIVE_SELL_WITHOUT_AGENT = 'pdf_request_btn_non-happy'
const CMP_NAME = 'residential_atlas_myproperty'
const CMP_POSITION = 'residential_realtorleadengine_valuation'
const CMP_HAPPY_PATH_ID = '10-051210'
const CMP_NON_HAPPY_PATH_ID = '10-051229'

const isSellWithAgent = (intent?: Intent): boolean => intent === Intent.MAKLER_SALE

const redirectToMyProperty = (state: State, pdfId: string): void => {
  const {
    lead,
    location: { data: location },
    address: { data: address },
  } = state

  const params = {
    pdfId,
    feature_myPropertyPdf: true,
    feature_priceValuationPdf: true,
    propertyType: lead.realEstateType,
    city: location?.city,
    zipcode: location?.postcode,
    street: address?.street,
    houseNumber: address?.houseNumber,
    rooms: lead.rooms,
    livingArea: lead.livingSpace,
    plotArea: lead.plotArea,
    constructionyear: lead?.constructionYear,
    furnishingType: lead?.furnishingType,
    cmp_id: isSellWithAgent(lead.intent) ? CMP_HAPPY_PATH_ID : CMP_NON_HAPPY_PATH_ID,
    cmp_name: CMP_NAME,
    cmp_position: CMP_POSITION,
    cmp_creative: isSellWithAgent(lead.intent) ? CMP_CREATIVE_SELL_WITH_AGENT : CMP_CREATIVE_SELL_WITHOUT_AGENT,
  }

  const myPropertyUrl = `${process.env.MY_PROPERTY_URL}?${stringify(params)}`

  window.location.href = myPropertyUrl
}

const createValuationPdf = createAsyncThunk<ValuationPdfResponse | void, void, ThunkStateConfig>(
  'valuationPdf/createPdf',
  async (params, thunkAPI) => {
    const state = thunkAPI.getState()
    const {
      lead: { id: leadId, ownership },
      config: { integrationType, isIframe },
    } = state

    const response = await clientCreateValuationPdf(leadId)

    thunkAPI.dispatch(
      reportTrackingEvent({
        label: 'pdf_completed',
        pageTitle: 'pdfloading',
        eventName: 'file_create',
        inputValue: '',
      })
    )
    const shouldRedirectToMyProperty =
      ((integrationType === IntegrationType.IMMOBILIENSCOUT24 && isIframe) || !isIframe) &&
      integrationType !== IntegrationType.IMMOVERKAUF24

    if (shouldRedirectToMyProperty && isOwnerOrPartialOwner(ownership)) {
      if (leadId) {
        thunkAPI.dispatch(
          enrichLead({
            pageTitle: 'pdfLoading',
            request: {
              id: leadId,
              flowComplete: true,
            },
          })
        )
      }
      return redirectToMyProperty(thunkAPI.getState(), response.pdfId)
    } else {
      return response
    }
  }
)

const getSignedUrl = createAsyncThunk<SignedUrlResponse, void, ThunkStateConfig>(
  'valuationPdf/getSignedUrl',
  async (params, thunkAPI) => {
    const {
      lead: { id: leadId },
      valuationPdf: { data: valuationPdf },
    } = thunkAPI.getState()

    return await clientGetSignedUrl(leadId, valuationPdf?.pdfId)
  }
)

export { createValuationPdf, getSignedUrl }
